import { Component, computed, inject, input, linkedSignal, model, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { _, TranslatePipe, TranslateService } from '@ngx-translate/core';
import { mgcIconCheckRegular } from '@sb/mgc-icons/mgcIcon-check_regular.icon';
import { featureFlag, FeatureService } from '@sb/shared/feature';
import { BadgeComponent } from '@sb/ui/badge';
import { ButtonComponent } from '@sb/ui/button';
import { CardModule } from '@sb/ui/card';
import { CheckboxComponent } from '@sb/ui/checkbox';
import { SbFormFieldComponent } from '@sb/ui/form-field';
import { IconRegistry } from '@sb/ui/icon';
import { NoticeComponent, NoticeHeaderDirective } from '@sb/ui/notice';
import { SelectComponent, type SelectItem } from '@sb/ui/select';
import { clsx } from 'clsx';

import { takeChosenFile } from '../chosen-docx.helper';
import type { DetectedField, Notice } from '../models/ai-template-builder.model';
import { signerStatus } from './signers.helper';

type GuardianCount = 0 | 1 | 2;

interface Signer {
  title: string;
  subtitle: string;
  found: boolean;
  alwaysSigns: boolean;
}

const GUARDIAN_TITLES = [_('Guardian'), _('Second guardian')];

/**
 * Step 2: who signs, in signing order. The store seeds guardianCount with the guardians the document already has.
 * A missing party is fixed outside the wizard, so the warning offers the document to download and to upload again.
 */
@Component({
  selector: 'who-signs-step',
  templateUrl: './who-signs-step.component.html',
  imports: [
    BadgeComponent,
    ButtonComponent,
    CardModule,
    CheckboxComponent,
    FormsModule,
    NoticeComponent,
    NoticeHeaderDirective,
    SbFormFieldComponent,
    SelectComponent,
    TranslatePipe,
  ],
  host: { '[class]': 'hostClasses' },
})
export class WhoSignsStepComponent {
  hostClasses = clsx('flex flex-col gap-lg');

  #translate = inject(TranslateService);
  #featureService = inject(FeatureService);

  fields = input.required<DetectedField[]>();
  notices = input<Notice[]>([]);
  isDownloading = input(false);
  downloadFailed = input(false);
  guardianCount = model<GuardianCount>(0);
  noDigitalSignature = model(false);

  download = output();
  upload = output<File>();

  constructor() {
    inject(IconRegistry).registerIcons([mgcIconCheckRegular]);
  }

  guardiansEnabled = this.#featureService.isEnabled(featureFlag.TMP_GUARDIAN_SIGNATURE);
  guardianCountEnabled = this.#featureService.isEnabled(featureFlag.TMP_GUARDIAN_SIGNATURE_COUNT);

  guardianCountItems: SelectItem<number>[] = [
    { value: 0, text: this.#translate.instant('No guardians') },
    { value: 1, text: this.#translate.instant('1 guardian') },
    { value: 2, text: this.#translate.instant('2 guardians') },
  ];

  rejectedFile = signal(false);

  /** What checking the box restores. It starts from the document, since leaving the step forgets what it was unchecked from. */
  uncheckedGuardianCount = linkedSignal<1 | 2>(() => signerStatus(this.fields()).guardians || 1);

  signers = computed<Signer[]>(() => {
    const { employer, employee, guardian, secondGuardian } = signerStatus(this.fields());
    const chosenGuardians = [guardian, secondGuardian].slice(0, this.guardianCount()).map(({ found }, index) => ({
      title: GUARDIAN_TITLES[index],
      subtitle: _('Signs for an employee under 18'),
      found,
      alwaysSigns: false,
    }));

    return [
      {
        title: _('Employer'),
        subtitle: _('Signs on behalf of your company'),
        found: employer.found,
        alwaysSigns: true,
      },
      { title: _('Employee'), subtitle: _('The person the contract is for'), found: employee.found, alwaysSigns: true },
      ...chosenGuardians,
    ];
  });

  hasMissingParty = computed(
    () =>
      this.signers().some(({ found, alwaysSigns }) => alwaysSigns && !found) ||
      this.notices().some(({ type }) => type === 'no_signature_block'),
  );

  setGuardianChecked(checked: boolean): void {
    const count = this.guardianCount();

    if (checked) {
      this.guardianCount.set(this.uncheckedGuardianCount());
    } else if (count !== 0) {
      this.uncheckedGuardianCount.set(count);
      this.guardianCount.set(0);
    }
  }

  chooseFile(fileInput: HTMLInputElement): void {
    const chosen = takeChosenFile(fileInput);

    if (!chosen) {
      return;
    }

    this.rejectedFile.set(!chosen.isDocx);

    if (chosen.isDocx) {
      this.upload.emit(chosen.file);
    }
  }
}
